<!-- eslint-disable -->
<template lang="pug">
    div.multiselect(
        :tabindex="searchable ? -1 : tabindex",
        :class=`{
            'multiselect--active': isOpen,
            'multiselect--disabled': disabled,
            'multiselect--above': isAbove,
            'multiselect--allow-empty': allowEmpty,
            'multiselect--multiple': multiple,
            'multiselect--error': error,
            'multiselect--empty': !value || !value.length
        }`,
        @focus="activate()",
        @blur="searchable ? false : deactivate()",
        @keydown.self.down.prevent="pointerForward()",
        @keydown.self.up.prevent="pointerBackward()",
        @keydown.enter.tab.stop.self="addPointerElement($event)",
        @keyup.esc="deactivate()"
    )
        svg-icon.multiselect__select(
            v-show="!loading",
            name="angle",
            :toggle="toggle",
            size="base",
            @mousedown.native.prevent.stop="toggle()"
        )
        slot(name="clear", :search="search")
        div.multiselect__tags(ref="tags")
            div.multiselect__tags-wrap(:class="{ 'multiselect__tags-with-limit' : tagsLimit }", v-if="visibleValues.length > 0")
                template(v-for="option of limitOfVisibleValues", @mousedown.prevent="")
                    slot(name="tag", :option="option", :search="search", :remove="removeElement")
                        div.multiselect__tag-container
                            span.multiselect__tag
                                span.multiselect__tag-text(v-text="getOptionLabel(option)")
                                svg-icon.multiselect__tag-icon(
                                    size="base"
                                    name="cancel",
                                    @keydown.native.enter.prevent="removeElement(option)",
                                    @mousedown.native.prevent="removeElement(option)"
                                )
            template(v-if="internalValue && internalValue.length > limit")
                slot(name="limit")
                    strong.multiselect__strong(v-text="limitText(internalValue.length - limit)")
            transition(name="multiselect__loading")
                slot(name="loading")
                    div.multiselect__spinner(v-show="loading")
            input.multiselect__input(
                ref="search",
                v-show="isOpen && searchable",
                :name="name",
                :id="id",
                type="text",
                autocomplete="off",
                :placeholder="placeholder",
                :style="inputStyle",
                :value="search",
                :disabled="disabled",
                :tabindex="tabindex",
                @input="updateSearch($event.target.value)",
                @focus.prevent="activate()",
                @blur.prevent="deactivate()",
                @keyup.esc="deactivate()",
                @keydown.down.prevent="pointerForward()",
                @keydown.up.prevent="pointerBackward()",
                @keydown.enter.prevent.stop.self="addPointerElement($event)",
                @keydown.delete.stop="removeLastElement()",
            )
            div.multiselect__single(
                v-if="isSingleLabelVisible",
                @mousedown.prevent="toggle"
                :title="isPlaceholderVisible ? '' : currentOptionLabel",
            )
                slot(name="inner-label")
                | {{ currentOptionLabel }}
            div.multiselect__placeloder(
                v-if="isPlaceholderVisible",
                @mousedown.prevent="toggle"
            ) {{ placeholder }}
        transition(name="multiselect")
            div(
                class="multiselect__content-wrapper",
                v-show="isOpen",
                @focus="activate",
                @mousedown.prevent="",
                :style="{ maxHeight: optimizedHeight + 'px' }",
                ref="list"
            )
                ul.multiselect__content(:style="contentStyle")
                    slot(name="beforeList")
                    li(v-if="multiple && max === internalValue.length")
                        span.multiselect__option
                            slot(name="maxElements") Maximum of {{ max }} options selected. First remove a selected option to select another.
                    template(v-if="!max || internalValue.length < max")
                        li.multiselect__element(v-for="(option, index) of filteredOptions", :key="index")
                            span.multiselect__option(
                                v-if="!(option && (option.$isLabel || option.$isDisabled))",
                                :class="optionHighlight(index, option)",
                                @click.stop="select(option)",
                                @mouseenter.self="pointerSet(index)"
                            )
                                slot(name="option", :option="option", :search="search")
                                    span {{ getOptionLabel(option) }}
                            span.multiselect__option(
                                v-if="option && (option.$isLabel || option.$isDisabled)",
                                :data-select="groupSelect && selectGroupLabelText",
                                :data-deselect="groupSelect && deselectGroupLabelText",
                                :class="groupHighlight(index, option)",
                                @mouseenter.self="groupSelect && pointerSet(index)",
                                @mousedown.prevent="selectGroup(option)"
                            )
                                slot(name="option", :option="option", :search="search")
                                    span {{ getOptionLabel(option) }}
                    li(v-show="showNoResults && (filteredOptions.length === 0 && search && !loading)")
                        span.multiselect__option
                            slot(name="noResult") No elements found. Consider changing the search query.
                    slot(name="afterList")
</template>

<script>
    /*eslint-disable*/
    import VueMultiselect from 'vue-multiselect';
    /**
     *  Multiselects are used for selecting multiple values.
     */
    export default {
        extends: VueMultiselect,
        name: 'CsMultiselect',
        status: 'ready',
        release: '1.0.0',
        props: {
            /**
             * Presets the selected options.
             */
            value: {
                type: [String, Number, Object, Array, Boolean],
                default: '',
            },
            /**
             * Array of available options.
             */
            options: {
                type: Array,
                default: () => [],
            },
            /**
             * Equivalent to the multiple attribute on a \<select\> input.
             */
            multiple: {
                type: Boolean,
                default: true,
            },
            /**
             * Used to compare objects. Only use if options are objects.
             */
            trackBy: {
                type: String,
            },
            /**
             * Label from option Object, that will be visible in the dropdown.
             */
            label: {
                type: String,
            },
            /**
             * Add / removes search input.
             */
            searchable: {
                type: Boolean,
                default: true,
            },
            /**
             * Clear the search input after select(). Use only when multiple is true.
             */
            clearOnSelect: {
                type: Boolean,
                default: true,
            },
            /**
             * Hide already selected options.
             */
            hideSelected: {
                type: Boolean,
                default: false,
            },
            /**
             * Equivalent to the placeholder attribute on a \<select\> input.
             */
            placeholder: {
                type: String,
                default: 'Placeholder',
            },
            /**
             * Allows to remove all selected values. Otherwise one must be left selected.
             */
            allowEmpty: {
                type: Boolean,
                default: true,
            },
            /**
             * Enable/disable closing after selecting an option.
             */
            closeOnSelect: {
                type: Boolean,
                default: true,
            },
            /**
             * Number of allowed selected options.
             */
            max: {
                type: Number,
            },
            /**
             * Limits the options displayed in the dropdown to the first X options.
             */
            optionsLimit: {
                type: Number,
                default: 1000,
            },
            /**
             * Selects the first option if initial value is empty.
             */
            preselectFirst: {
                type: Boolean,
                default: false,
            },
            /**
             * Decide whether to show labels on highlighted options.
             */
            showLabels: {
                type: Boolean,
                default: true,
            },
            /**
             * Limit the display of selected options. The rest will be hidden within the limitText string.
             */
            limit: {
                type: Number,
                default: 99999,
            },
            /**
             * Function that process the message shown when selected elements pass the defined limit.
             */
            limitText: {
                type: Function,
                default: count => `and ${count} more`,
            },
            /**
             * Show/hide the loading spinner.
             */
            loading: {
                type: Boolean,
                default: false,
            },
            /**
             * Enable/disable the multiselect.
             */
            disabled: {
                type: Boolean,
                default: false,
            },
            /**
             * Sets max-height style value of the dropdown.
             */
            maxHeight: {
                type: Number,
                default: 300,
            },
            /**
             * Fixed opening direction (instead of auto). Options are "above"/"top" or "below"/"bottom"
             */
            openDirection: {
                type: String,
                default: 'auto',
                validator: value => value.match(/(auto|above|top|below|bottom)/),
            },
            /**
             * Show the noResult slot if no results are found.
             */
            showNoResults: {
                type: Boolean,
                default: true,
            },
            /**
             * The error state of multiselect.
             */
            error: {
                type: Boolean,
                default: false,
            },
            tagsLimit: {
                type: Number,
                default: 0,
            },
        },
        computed: {
            limitOfVisibleValues() {
                return this.visibleValues.slice(-this.tagsLimit);
            },
            isSingleLabelVisible() {
                return (
                    (this.singleValue || this.singleValue === 0) &&
                    (!this.isOpen || !this.searchable) &&
                    !this.visibleValues.length
                );
            },
        },
    };
</script>

<style src="vue-multiselect/dist/vue-multiselect.min.css" scoped></style>

<style lang="scss" scoped>
    $icon-width: 32px;
    $border-width: 1px;
    $min-multiselect-height: 40px;

    .multiselect__tags {
        @include text;

        min-height: $min-multiselect-height;
        display: flex;
        flex-wrap: wrap;

        box-sizing: border-box;

        outline: none;

        color: $x-color-black;

        width: 100%;

        border-radius: $border-radius-6;
        border: 1px solid $hsl-color-gray-74;

        font-weight: normal;
        font-size: $font-size-16;
        line-height: $font-size-19;

        //@include inset-squish-space($space-small);

        padding: 12px 15px;

        &:hover {
            border-color: $hsl-color-blue-56;
        }

        &:focus {
            border-color: $hsl-color-blue-56;
        }

        &:active {
            border-color: $hsl-color-blue-56;
        }

        &::placeholder {
            font-weight: lighter;
        }
    }

    .multiselect--active:not(.multiselect--above) .multiselect__current,
    .multiselect--active:not(.multiselect--above) .multiselect__input,
    .multiselect--active:not(.multiselect--above) .multiselect__tags {
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
    }

    .multiselect--above.multiselect--active .multiselect__current,
    .multiselect--above.multiselect--active .multiselect__input,
    .multiselect--above.multiselect--active .multiselect__tags {
        border-top-left-radius: 0;
        border-top-right-radius: 0;
    }

    .multiselect--disabled {
        border-radius: $border-radius-6;
        color: $hsl-color-gray-53;
        .multiselect__select {
            background: $x-color-white;
        }
        .multiselect__placeloder {
            color: $hsl-color-gray-53;
        }
    }

    .multiselect__option {
        //@include inset-squish-space($space-small);
        padding: 12px 15px;
        font-family: 'Rubik', Helvetica, Arial, sans-serif;
        min-height: 0;
    }

    .multiselect__tags-wrap {
        max-width: 100%;
        height: auto;
        display: inline-flex;
        flex-wrap: wrap;
        margin: -$space-xx-small;
        // full size except loader or arrow icon
        width: calc(100% - #{$icon-width});
        word-break: break-all;
    }

    .multiselect__tags-with-limit {
        flex-wrap: nowrap;
    }

    .multiselect__tag-container {
        white-space: nowrap;
        flex: none;
        overflow: hidden;
        text-overflow: ellipsis;
        @include inset-space($space-xx-small);
    }

    .multiselect__tag {
        background-color: $hsl-color-blue-56;
        vertical-align: middle;
        margin: 0;
        display: flex;
        align-items: center;
        @include inset-squish-space($space-x-small);
        @include text($font-size-14);
    }

    .multiselect__tag-icon {
        height: 100%;
        position: static;
        margin: 0;
        width: auto;
        &:after,
        &:before {
            display: none;
        }
        &:hover {
            color: $hsl-color-red-62;
            background-color: $hsl-color-blue-56;
        }
    }

    .multiselect__tag-text {
        overflow: hidden;
        text-overflow: ellipsis;
        @include inline-space($space-x-small);
    }

    .multiselect__content-wrapper {
        position: absolute;
        display: block;
        background: #fff;
        width: 100%;
        max-height: 240px;
        overflow: auto;
        border: $border-width solid $hsl-color-gray-74;
        border-top: none;
        border-bottom-left-radius: 5px;
        border-bottom-right-radius: 5px;
        z-index: 50;
    }

    .multiselect__single,
    .multiselect__input {
        border-radius: 0;
        // full size except loader or arrow icon
        width: calc(100% - #{$icon-width}) !important;
        @include inset-space(0);
        margin: 0;
        min-height: 0;
        @include text($font-size-16);
        color: $color-shark;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        &::placeholder {
            @include text($font-size-16);
            color: $hsl-color-gray-51;
        }
    }

    .multiselect--multiple {
        .multiselect__input {
            @include inset-space($space-xx-small);
            padding-top: 8px;
        }
    }

    .multiselect--empty {
        .multiselect__input {
            padding: 0;
        }
    }

    .multiselect__placeloder {
        @include text($font-size-16);
        color: $hsl-color-gray-51;
        // full size except loader or arrow icon
        width: calc(100% - #{$icon-width});
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .multiselect__option {
        @include text($font-size-16);
    }

    .multiselect__option--highlight {
        color: $x-color-black;
        background-color: $hsl-color-gray-98;
    }

    .multiselect__option--selected {
        background-color: $hsl-color-gray-98;
    }

    .multiselect--allow-empty {
        .multiselect__option--highlight.multiselect__option--selected {
            background: $hsl-color-red-62;
        }
    }

    .multiselect__select {
        color: $hsl-color-blue-56;
        border-radius: $border-radius-6;
        height: calc(100% - 2 * #{$border-width});
        padding: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        &:before {
            display: none;
        }
    }

    .multiselect__spinner {
        height: calc(100% - 2 * #{$border-width});
        border-radius: $border-radius-6;
        display: flex;
        align-items: center;
        justify-content: center;
        background: none;
        &:before {
            display: none;
        }
        &:after {
            border-top-color: $hsl-color-blue-56;
            position: static;
            margin: 0;
            background: transparent;
        }
    }
</style>
